import { useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import {
  ArrowDown,
  ArrowDownToLine,
  BadgeCheck,
  BatteryMedium,
  Car,
  ChartLine,
  ChevronDown,
  CircleHelp,
  Columns3,
  KeyRound,
  Lock,
  LockOpen,
  Minimize2,
  Minus,
  MoonStar,
  Plus,
  Power,
  RefreshCw,
  Thermometer,
  Timer,
  TriangleAlert,
  UserCheck,
  Wind,
  X
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type {
  TeslaBatteryDrainBucket,
  TeslaBatteryHistory,
  TeslaChargeSession,
  TeslaConnectionState,
  TeslaVehicleState
} from "@home-control/shared";
import { SectionHeader } from "../components/SectionHeader";
import { StatusPill } from "../components/StatusPill";
import { Segmented } from "../components/Segmented";
import { Spinner } from "../components/Spinner";
import { useHome } from "../state";
import { relativeTime } from "../format";
import {
  fetchTeslaHistory,
  fetchTeslaStatus,
  teslaConnectUrl,
  TeslaHistoryUnavailableError
} from "./tesla-api";
import "./tesla.css";

const ventDeadlineKey = "teslaVentCloseDeadline";
const ventDurationMs = 5 * 60 * 1000;
const connectedMarkerParam = "tesla_connected";

const defaultConnection: TeslaConnectionState = {
  status: "notConnected",
  connected: false,
  canCommand: false,
  canWake: false
};

/** Statuses that block commands even while linked (spec §2.4). */
const commandBlockedStatuses = new Set(["notConnected", "authRefreshing", "reconnectRequired", "rateLimited"]);

function connectionLabel(connection: TeslaConnectionState): string {
  switch (connection.status) {
    case "notConnected":
      return "Not connected";
    case "authRefreshing":
      return "Connecting";
    case "reconnectRequired":
      return "Reconnect required";
    case "rateLimited":
      return "Rate limited";
    case "vehicleAsleep":
      return "Asleep";
    case "vehicleOnline":
      return "Online";
    case "connected":
      return "Linked";
    case "vehicleUnavailable":
      return "Vehicle unavailable";
    default:
      return connection.connected ? "Linked" : "Not connected";
  }
}

function lastKnownStateAt(vehicle: TeslaVehicleState, stateUpdatedAt: string | undefined): string | null {
  return (
    vehicle.lastStateAt ??
    vehicle.lastSeenAt ??
    vehicle.lastUpdatedAt ??
    vehicle.updatedAt ??
    vehicle.lastContactedAt ??
    stateUpdatedAt ??
    null
  );
}

function readVentDeadline(): number | null {
  const raw = Number(localStorage.getItem(ventDeadlineKey));
  if (!Number.isFinite(raw) || raw <= Date.now()) {
    localStorage.removeItem(ventDeadlineKey);
    return null;
  }
  return raw;
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function TeslaPage() {
  const { state, token, command, isPending, canControlDevices } = useHome();
  const vehicle = state?.tesla ?? null;

  // Fallback to GET /tesla/status when /state omits teslaConnection (spec §2).
  const [statusFallback, setStatusFallback] = useState<TeslaConnectionState | null>(null);
  const missingConnection = state !== null && state.teslaConnection === undefined;
  useEffect(() => {
    if (!token || !missingConnection) return;
    let cancelled = false;
    fetchTeslaStatus(token)
      .then((connection) => {
        if (!cancelled) setStatusFallback(connection);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, missingConnection]);
  const connection = state?.teslaConnection ?? statusFallback ?? defaultConnection;

  // Gating (spec §2.4).
  const isReadOnly = !canControlDevices;
  const canSendTeslaCommand = connection.connected && !commandBlockedStatuses.has(connection.status);
  const canCommand = !isReadOnly && canSendTeslaCommand;
  const canWake = !isReadOnly && (connection.canWake || canSendTeslaCommand);

  // "Tesla linked." toast after returning from the OAuth redirect (spec §2.3).
  const [linkedToast, setLinkedToast] = useState(false);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(connectedMarkerParam)) return;
    url.searchParams.delete(connectedMarkerParam);
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    setLinkedToast(true);
  }, []);
  useEffect(() => {
    if (!linkedToast) return;
    const timer = setTimeout(() => setLinkedToast(false), 5000);
    return () => clearTimeout(timer);
  }, [linkedToast]);

  // Vent-close countdown — client display of the server's 5-minute auto close (spec §4.3).
  const [ventDeadline, setVentDeadline] = useState<number | null>(() => readVentDeadline());
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (ventDeadline === null) return;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [ventDeadline]);
  const remainingSeconds = ventDeadline === null ? 0 : Math.max(0, Math.ceil((ventDeadline - nowMs) / 1000));
  useEffect(() => {
    if (ventDeadline !== null && remainingSeconds <= 0) {
      localStorage.removeItem(ventDeadlineKey);
      setVentDeadline(null);
    }
  }, [ventDeadline, remainingSeconds]);

  function startVentCountdown() {
    const deadline = Date.now() + ventDurationMs;
    localStorage.setItem(ventDeadlineKey, String(deadline));
    setNowMs(Date.now());
    setVentDeadline(deadline);
  }

  function clearVentCountdown() {
    localStorage.removeItem(ventDeadlineKey);
    setVentDeadline(null);
  }

  // Cabin target stepper, synced from the vehicle (spec §4.1).
  const climateOn = vehicle?.climateOn === true;
  const syncedTarget = vehicle?.driverTempSettingF ?? null;
  const [target, setTarget] = useState(() => Math.round(syncedTarget ?? 70));
  useEffect(() => {
    if (syncedTarget !== null) setTarget(Math.round(syncedTarget));
  }, [syncedTarget]);

  function stepTarget(delta: number) {
    const next = Math.min(80, Math.max(60, target + delta));
    if (next === target) return;
    setTarget(next);
    void command("tesla-target", { kind: "setTeslaCabinTarget", targetTempF: next });
  }

  // Header content (spec §1).
  const title = vehicle?.displayName ?? "Tesla";
  let statusLine: string;
  if (!vehicle) {
    statusLine = connectionLabel(connection);
  } else {
    const base = vehicle.online === false ? "Offline" : vehicle.sentryMode === true ? "Online - Sentry" : "Online";
    const stateAt = lastKnownStateAt(vehicle, state?.updatedAt);
    statusLine = stateAt ? `${base} - last state ${relativeTime(stateAt)}` : base;
  }
  const pill = vehicle
    ? vehicle.locked === true
      ? { text: "Locked", tint: "var(--cool)" }
      : vehicle.locked === false
        ? { text: "Unlocked", tint: "var(--warm)" }
        : { text: "Unknown", tint: "var(--warm)" }
    : { text: connection.connected ? "Linked" : "Setup", tint: "var(--warm)" };

  // Quick actions (spec §4.2).
  const windowsOpen = vehicle?.windowsOpen === true;
  const trunkOpen = vehicle?.rearTrunkOpen === true;
  const locked = vehicle?.locked === true;
  const windowsPending = isPending("tesla-windows-vent") || isPending("tesla-windows-close");
  const lockPending = isPending("tesla-unlock") || isPending("tesla-lock");
  const trunkPending = isPending("tesla-trunk-open") || isPending("tesla-trunk-close");

  function toggleWindows() {
    if (windowsOpen) {
      clearVentCountdown();
      void command("tesla-windows-close", { kind: "closeTeslaWindows" });
    } else {
      void command("tesla-windows-vent", { kind: "ventTeslaWindows" });
    }
  }

  function toggleLock() {
    if (locked) {
      const confirmed = window.confirm(
        "Unlock Tesla?\n\nThis can leave the vehicle accessible until it is locked again."
      );
      if (!confirmed) return;
      void command("tesla-unlock", { kind: "setTeslaLock", locked: false });
    } else {
      void command("tesla-lock", { kind: "setTeslaLock", locked: true });
    }
  }

  function toggleClimate() {
    const enabled = !climateOn;
    if (!enabled) clearVentCountdown();
    void command("tesla-climate", { kind: "setTeslaClimate", enabled });
  }

  function coolAndVent() {
    startVentCountdown();
    void command("tesla-70-vent", { kind: "setTeslaCabinTo70AndVent" });
  }

  return (
    <main className="page">
      <SectionHeader title="Tesla" />

      <section className="panel tsl-panel">
        <header className="tsl-header">
          <span className="tsl-header-icon">
            <Car size={22} strokeWidth={2.4} />
          </span>
          <span className="tsl-header-text">
            <span className="tsl-header-title">{title}</span>
            <span className="tsl-header-status">{statusLine}</span>
          </span>
          <StatusPill text={pill.text} tint={pill.tint} />
        </header>

        {!connection.connected ? (
          <ConnectPrompt connection={connection} />
        ) : (
          <>
            <div className="tsl-metrics">
              <Metric
                icon={BatteryMedium}
                value={vehicle?.batteryLevel != null ? `${vehicle.batteryLevel}%` : "--"}
                label="Battery"
              />
              <Metric
                icon={Thermometer}
                value={vehicle?.cabinTempF != null ? `${Math.round(vehicle.cabinTempF)}°` : "--"}
                label="Cabin"
              />
              <Metric
                icon={Columns3}
                value={vehicle?.windowsOpen == null ? "--" : vehicle.windowsOpen ? "Vented" : "Closed"}
                label="Windows"
              />
            </div>

            <div className="tsl-climate">
              <div className="tsl-climate-head">
                <span className="tsl-climate-title">Climate</span>
                {isPending("tesla-climate") || isPending("tesla-target") ? <Spinner size={18} /> : null}
              </div>
              <div className="tsl-climate-row">
                <button
                  type="button"
                  className={`tsl-climate-toggle${climateOn ? " on" : ""}`}
                  disabled={!canCommand || isPending("tesla-climate")}
                  aria-pressed={climateOn}
                  onClick={toggleClimate}
                >
                  <Power size={17} strokeWidth={2.6} />
                  {climateOn ? "On" : "Off"}
                </button>
                <div className="tsl-stepper">
                  <button
                    type="button"
                    className="tsl-step-btn"
                    aria-label="Lower cabin target"
                    disabled={!canCommand || target <= 60 || isPending("tesla-target")}
                    onClick={() => stepTarget(-1)}
                  >
                    <Minus size={14} strokeWidth={3.4} />
                  </button>
                  <span className="tsl-step-value">{target}°</span>
                  <button
                    type="button"
                    className="tsl-step-btn"
                    aria-label="Raise cabin target"
                    disabled={!canCommand || target >= 80 || isPending("tesla-target")}
                    onClick={() => stepTarget(1)}
                  >
                    <Plus size={14} strokeWidth={3.4} />
                  </button>
                </div>
              </div>
            </div>

            {remainingSeconds > 0 ? (
              <div className="tsl-vent-bar">
                <Timer size={13} strokeWidth={2.6} />
                <span className="tsl-vent-text">
                  Cloud closes windows in {formatCountdown(remainingSeconds)}
                </span>
              </div>
            ) : null}

            <div className="tsl-actions">
              <QuickAction
                icon={Power}
                title="Wake"
                pending={isPending("tesla-wake")}
                enabled={canWake}
                onPress={() => void command("tesla-wake", { kind: "wakeTesla" })}
              />
              <QuickAction
                icon={Wind}
                title="70° + Vent"
                pending={isPending("tesla-70-vent")}
                enabled={canCommand}
                onPress={coolAndVent}
              />
              <QuickAction
                icon={windowsOpen ? Minimize2 : Columns3}
                title={windowsOpen ? "Close Windows" : "Vent Windows"}
                pending={windowsPending}
                enabled={canCommand}
                onPress={toggleWindows}
              />
              <QuickAction
                icon={locked ? LockOpen : Lock}
                title={locked ? "Unlock" : "Lock"}
                pending={lockPending}
                enabled={canCommand}
                onPress={toggleLock}
              />
              <QuickAction
                icon={Car}
                title="Open Front"
                pending={isPending("tesla-frunk")}
                enabled={canCommand}
                onPress={() => void command("tesla-frunk", { kind: "openTeslaFrunk" })}
              />
              <QuickAction
                icon={trunkOpen ? ArrowDownToLine : Car}
                title={trunkOpen ? "Close Trunk" : "Open Trunk"}
                pending={trunkPending}
                enabled={canCommand}
                onPress={() =>
                  trunkOpen
                    ? void command("tesla-trunk-close", { kind: "closeTeslaTrunk" })
                    : void command("tesla-trunk-open", { kind: "openTeslaTrunk" })
                }
              />
            </div>
          </>
        )}

        {vehicle && vehicle.online === false ? (
          <p className="tsl-note">Showing last state. Controls will wake the car first.</p>
        ) : null}
        {isReadOnly ? <p className="tsl-note">Updating latest car state. Controls unlock after sync.</p> : null}

        {connection.connected ? <BatteryHistorySection token={token} /> : null}
      </section>

      {linkedToast ? (
        <div className="toast" role="status">
          <BadgeCheck size={20} strokeWidth={2.4} style={{ color: "var(--cool)" }} />
          <p className="toast-message">Tesla linked.</p>
          <button type="button" className="toast-dismiss" aria-label="Dismiss" onClick={() => setLinkedToast(false)}>
            <X size={12} strokeWidth={3.2} />
          </button>
        </div>
      ) : null}
    </main>
  );
}

function Metric({ icon: Icon, value, label }: { icon: LucideIcon; value: string; label: string }) {
  return (
    <div className="tsl-metric">
      <Icon size={15} strokeWidth={2.6} />
      <span className="tsl-metric-value">{value}</span>
      <span className="tsl-metric-label">{label}</span>
    </div>
  );
}

function QuickAction({
  icon: Icon,
  title,
  pending,
  enabled,
  onPress
}: {
  icon: LucideIcon;
  title: string;
  pending: boolean;
  enabled: boolean;
  onPress: () => void;
}) {
  return (
    <button type="button" className="tsl-action" disabled={!enabled || pending} onClick={onPress}>
      <span className="tsl-icon-slot">{pending ? <Spinner size={16} /> : <Icon size={19} strokeWidth={2.5} />}</span>
      <span className="tsl-action-title">{title}</span>
    </button>
  );
}

/* Connect prompt (spec §2.2 / §2.3) ---------------------------------------- */

function ConnectPrompt({ connection }: { connection: TeslaConnectionState }) {
  const [connecting, setConnecting] = useState(false);
  const refreshing = connection.status === "authRefreshing";
  const reconnect = connection.status === "reconnectRequired";
  const Icon = refreshing || reconnect ? RefreshCw : KeyRound;
  const promptTitle = refreshing ? "Checking Tesla" : reconnect ? "Reconnect Tesla" : "Sign in to Tesla";
  const buttonLabel = connecting ? "Connecting" : refreshing ? "Checking" : reconnect ? "Reconnect Tesla" : "Connect Tesla";

  function startConnect() {
    setConnecting(true);
    const callback = new URL(window.location.href);
    callback.searchParams.set(connectedMarkerParam, "1");
    window.location.assign(teslaConnectUrl(callback.toString()));
  }

  return (
    <div className="tsl-connect">
      <div className="tsl-connect-row">
        <span className="tsl-connect-icon">
          <Icon size={18} strokeWidth={2.8} />
        </span>
        <span className="tsl-connect-title">{promptTitle}</span>
      </div>
      <p className="tsl-connect-subtitle">{connection.message ?? "Link your account to enable car controls."}</p>
      <button type="button" className="tsl-connect-btn" disabled={connecting || refreshing} onClick={startConnect}>
        <span className="tsl-icon-slot">
          {connecting ? <Spinner size={16} /> : <UserCheck size={19} strokeWidth={2.5} />}
        </span>
        {buttonLabel}
      </button>
    </div>
  );
}

/* Battery history (spec §5) -------------------------------------------------- */

type HistoryRange = "24h" | "7d";
type CauseFilter = "all" | "climateIdle" | "driving" | "phantomIdle";
type BucketKind = "climateIdle" | "driving" | "phantomIdle" | "other";

const bucketMeta: Record<BucketKind, { label: string; short: string; tint: string; icon: LucideIcon }> = {
  climateIdle: { label: "A/C while parked", short: "A/C", tint: "var(--cool)", icon: Wind },
  driving: { label: "Driving", short: "Drive", tint: "var(--warm)", icon: Car },
  phantomIdle: { label: "Phantom idle", short: "Idle", tint: "var(--phantom)", icon: MoonStar },
  other: { label: "Other", short: "Other", tint: "var(--muted)", icon: CircleHelp }
};

const preferredBucketOrder: BucketKind[] = ["climateIdle", "driving", "phantomIdle", "other"];

function canonicalKind(raw: string | null | undefined): BucketKind {
  const kind = (raw ?? "").toLowerCase().replace(/[-\s]+/g, "_");
  if (["climate_idle", "climateidle", "climate", "ac", "a_c", "hvac"].includes(kind)) return "climateIdle";
  if (["driving", "drive"].includes(kind)) return "driving";
  if (["phantom_idle", "phantomidle", "phantom", "idle", "standby"].includes(kind)) return "phantomIdle";
  return "other";
}

/** "12%" when within 0.05 of an integer, else one decimal (spec §5.8). */
function formatPercent(value: number): string {
  const rounded = Math.round(value);
  return Math.abs(value - rounded) < 0.05 ? `${rounded}%` : `${value.toFixed(1)}%`;
}

function isToday(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function formatHourLabel(date: Date): string {
  const hour24 = date.getHours();
  const suffix = hour24 >= 12 ? "PM" : "AM";
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour}${suffix}`;
}

function formatClock(date: Date): string {
  const hour24 = date.getHours();
  const suffix = hour24 >= 12 ? "PM" : "AM";
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour}:${String(date.getMinutes()).padStart(2, "0")} ${suffix}`;
}

function formatMonthDay(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Non-date synthetic point labels (spec §5.6). */
function syntheticLabel(raw: string): string {
  switch (raw) {
    case "start":
      return "Start";
    case "A/C while parked":
      return "A/C";
    case "Driving":
      return "Drive";
    case "Phantom idle":
      return "Idle";
    default:
      return raw.slice(0, 8);
  }
}

function axisLabel(raw: string): string {
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return syntheticLabel(raw);
  const date = new Date(parsed);
  return isToday(date) ? formatHourLabel(date) : formatMonthDay(date);
}

function tooltipTimestamp(raw: string): string {
  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return syntheticLabel(raw);
  const date = new Date(parsed);
  return isToday(date) ? formatClock(date) : `${formatMonthDay(date)} ${formatClock(date)}`;
}

function bucketPercent(session: TeslaChargeSession, kind: BucketKind): number {
  return session.buckets.reduce(
    (total, bucket) => (canonicalKind(bucket.kind) === kind ? total + bucket.percent : total),
    0
  );
}

function filterSessions(sessions: TeslaChargeSession[], filter: CauseFilter): TeslaChargeSession[] {
  switch (filter) {
    case "all":
      return sessions;
    case "climateIdle":
      return sessions.filter((session) => bucketPercent(session, "climateIdle") > 0);
    case "driving":
      return sessions.filter((session) => bucketPercent(session, "driving") > 0);
    case "phantomIdle":
      return sessions.filter((session) => bucketPercent(session, "phantomIdle") >= 1);
  }
}

function BatteryHistorySection({ token }: { token: string }) {
  const [expanded, setExpanded] = useState(false);
  const [range, setRange] = useState<HistoryRange>("24h");
  const [filter, setFilter] = useState<CauseFilter>("all");
  const [visibleCount, setVisibleCount] = useState(2);
  const [histories, setHistories] = useState<Partial<Record<HistoryRange, TeslaBatteryHistory>>>({});
  const [errors, setErrors] = useState<Partial<Record<HistoryRange, string>>>({});
  const [loading, setLoading] = useState<Partial<Record<HistoryRange, boolean>>>({});

  const history = histories[range];
  const isLoading = loading[range] === true;
  const error = errors[range];

  async function load(targetRange: HistoryRange, cursor?: string) {
    setLoading((current) => ({ ...current, [targetRange]: true }));
    setErrors((current) => ({ ...current, [targetRange]: undefined }));
    try {
      const next = await fetchTeslaHistory(token, targetRange, cursor);
      setHistories((current) => {
        if (!cursor) return { ...current, [targetRange]: next };
        const existing = current[targetRange];
        if (!existing) return { ...current, [targetRange]: next };
        // Load-older appends sessions and replaces the cursor (spec §5.5).
        return {
          ...current,
          [targetRange]: {
            ...existing,
            sessions: [...(existing.sessions ?? []), ...(next.sessions ?? [])],
            nextCursor: next.nextCursor,
            totalSessionCount: next.totalSessionCount ?? existing.totalSessionCount
          }
        };
      });
      if (cursor) setVisibleCount((count) => count + 2);
    } catch (loadError) {
      const message =
        loadError instanceof TeslaHistoryUnavailableError
          ? loadError.message
          : loadError instanceof Error && loadError.message
            ? loadError.message
            : "Battery history request failed.";
      setErrors((current) => ({ ...current, [targetRange]: message }));
    } finally {
      setLoading((current) => ({ ...current, [targetRange]: false }));
    }
  }

  useEffect(() => {
    if (!expanded || histories[range] || loading[range] || errors[range]) return;
    void load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, range]);

  function changeRange(next: HistoryRange) {
    setRange(next);
    setVisibleCount(2);
    setFilter("all");
  }

  function changeFilter(next: CauseFilter) {
    setFilter(next);
    setVisibleCount(2);
  }

  const sessions = useMemo(
    () => filterSessions(history?.sessions ?? [], filter),
    [history, filter]
  );
  const hasData =
    history !== undefined &&
    (history.totalDrainPercent > 0 ||
      history.buckets.length > 0 ||
      history.points.length > 0 ||
      (history.sessions?.length ?? 0) > 0);

  return (
    <>
      <button type="button" className="tsl-disclosure" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
        <span className="tsl-disclosure-icon">
          <ChartLine size={16} strokeWidth={2.8} />
        </span>
        <span className="tsl-disclosure-text">
          <span className="tsl-disclosure-title">Battery history</span>
          <span className="tsl-disclosure-sub">Drain by charge session</span>
        </span>
        <ChevronDown size={15} strokeWidth={3} className={`tsl-disclosure-chevron${expanded ? " open" : ""}`} />
      </button>

      {expanded ? (
        <div className="tsl-history">
          <div className="tsl-history-head">
            <span className="tsl-history-title">Battery history</span>
            <div className="tsl-range-picker">
              <Segmented
                options={[
                  { value: "24h", label: "24h" },
                  { value: "7d", label: "7d" }
                ]}
                value={range}
                tint="var(--ink)"
                onChange={changeRange}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="tsl-history-loading">
              <Spinner size={20} />
              Loading battery history
            </div>
          ) : error ? (
            <div className="tsl-history-message">
              <TriangleAlert size={22} strokeWidth={2.4} style={{ color: "var(--warm)" }} />
              <span className="tsl-history-message-title">Battery history unavailable</span>
              <span className="tsl-history-message-body">{error}</span>
            </div>
          ) : history && hasData ? (
            <HistoryData
              history={history}
              sessions={sessions}
              filter={filter}
              visibleCount={visibleCount}
              isLoading={isLoading}
              onFilterChange={changeFilter}
              onShowMore={() => {
                if (visibleCount < sessions.length) {
                  setVisibleCount((count) => count + 2);
                } else if (history.nextCursor) {
                  void load(range, history.nextCursor);
                }
              }}
            />
          ) : (
            <div className="tsl-history-message">
              <BatteryMedium size={24} strokeWidth={2.4} style={{ color: "var(--cool)" }} />
              <span className="tsl-history-message-title">No battery history yet</span>
              <span className="tsl-history-message-body">
                Drain attribution will appear after Tesla snapshots are recorded.
              </span>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}

function HistoryData({
  history,
  sessions,
  filter,
  visibleCount,
  isLoading,
  onFilterChange,
  onShowMore
}: {
  history: TeslaBatteryHistory;
  sessions: TeslaChargeSession[];
  filter: CauseFilter;
  visibleCount: number;
  isLoading: boolean;
  onFilterChange: (filter: CauseFilter) => void;
  onShowMore: () => void;
}) {
  const visibleSessions = sessions.slice(0, visibleCount);
  const shownCount = Math.min(visibleCount, sessions.length);
  const totalCount =
    filter === "all" ? Math.max(history.totalSessionCount ?? 0, sessions.length) : sessions.length;
  const hasMoreLocal = visibleCount < sessions.length;
  const canLoadMore = hasMoreLocal || history.nextCursor != null;
  const batteryRange =
    history.startBatteryLevel != null && history.endBatteryLevel != null
      ? `${Math.round(history.startBatteryLevel)}% to ${Math.round(history.endBatteryLevel)}%`
      : "--";
  const trendPoints: ChartPoint[] = history.points
    .filter((point) => point.batteryLevel != null)
    .map((point) => ({
      label: point.timestamp,
      batteryLevel: point.batteryLevel as number,
      bucket: point.bucket == null ? null : canonicalKind(point.bucket)
    }));

  const filterOptions: { value: CauseFilter; label: string; tint: string }[] = [
    { value: "all", label: "All causes", tint: "var(--ink)" },
    { value: "climateIdle", label: "A/C parked", tint: "var(--cool)" },
    { value: "driving", label: "Driving", tint: "var(--warm)" },
    { value: "phantomIdle", label: "Phantom idle", tint: "var(--phantom)" }
  ];

  return (
    <>
      <div className="tsl-metrics">
        <div className="tsl-metric white">
          <ArrowDown size={14} strokeWidth={2.6} />
          <span className="tsl-metric-value">{formatPercent(history.totalDrainPercent)}</span>
          <span className="tsl-metric-label">Total drain</span>
        </div>
        <div className="tsl-metric white">
          <BatteryMedium size={14} strokeWidth={2.6} />
          <span className="tsl-metric-value">{batteryRange}</span>
          <span className="tsl-metric-label">Battery</span>
        </div>
      </div>

      {sessions.length > 0 || filter !== "all" ? (
        <>
          <div className="tsl-card-white">
            <div className="tsl-sessions-head">
              <div>
                <div className="tsl-sessions-title">Charge sessions</div>
                <div className="tsl-sessions-sub">Each chart starts when charging stops</div>
              </div>
              <span className="tsl-count">
                {shownCount} of {totalCount}
              </span>
            </div>
            <div className="tsl-chips">
              {filterOptions.map((option) => {
                const selected = option.value === filter;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`tsl-chip${selected ? " selected" : ""}`}
                    style={
                      selected
                        ? undefined
                        : {
                            color: option.tint,
                            background: `color-mix(in srgb, ${option.tint} 14%, transparent)`
                          }
                    }
                    aria-pressed={selected}
                    onClick={() => onFilterChange(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <div className="tsl-sessions-foot">
              <span className="tsl-sessions-foot-note">Older sessions stay collapsed until requested.</span>
              <button type="button" className="tsl-more-btn" disabled={!canLoadMore || isLoading} onClick={onShowMore}>
                {hasMoreLocal ? "Show more" : "Load older"}
                {isLoading ? <Spinner size={12} /> : null}
              </button>
            </div>
          </div>

          {visibleSessions.map((session) => (
            <SessionCard key={session.id} session={session} />
          ))}
        </>
      ) : (
        <>
          {trendPoints.length >= 2 ? (
            <BatteryChart points={trendPoints} height={92} showBucketFills={false} interactive={false} />
          ) : null}
          <DrainRows buckets={history.buckets} totalDrainPercent={history.totalDrainPercent} />
        </>
      )}
    </>
  );
}

/* Charge-session card (spec §5.6) ------------------------------------------- */

function SessionCard({ session }: { session: TeslaChargeSession }) {
  const chargeEnd = session.unpluggedAt ?? session.chargedAt;
  const title = session.title ?? (chargeEnd ? `After ${relativeTime(chargeEnd)} charge` : "Post-charge session");
  const subtitle =
    session.subtitle ??
    (session.startBatteryLevel != null && session.endBatteryLevel != null
      ? `${Math.round(session.startBatteryLevel)}% unplugged to ${Math.round(session.endBatteryLevel)}%`
      : "Battery use after charging stopped");

  let points: ChartPoint[] = session.points
    .filter((point) => point.batteryLevel != null)
    .map((point) => ({
      label: point.timestamp,
      batteryLevel: point.batteryLevel as number,
      bucket: point.bucket == null ? null : canonicalKind(point.bucket)
    }));
  if (points.length < 2) {
    // Synthesize a stepped line from the drain buckets (spec §5.6).
    let level = session.startBatteryLevel ?? 80;
    const synthesized: ChartPoint[] = [{ label: "start", batteryLevel: level, bucket: null }];
    for (const bucket of [...session.buckets].sort((a, b) => b.percent - a.percent)) {
      const kind = canonicalKind(bucket.kind);
      level -= bucket.percent;
      synthesized.push({ label: bucketMeta[kind].label, batteryLevel: level, bucket: kind });
    }
    points = synthesized.length >= 2 ? synthesized : [];
  }

  const pillBuckets = session.buckets
    .filter((bucket) => bucket.percent > 0)
    .sort((a, b) => b.percent - a.percent)
    .slice(0, 4);

  return (
    <div className="tsl-card-white">
      <div className="tsl-session-head">
        <div>
          <div className="tsl-session-title">{title}</div>
          <div className="tsl-session-sub">{subtitle}</div>
        </div>
        <div className="tsl-session-drain">
          <span className="tsl-session-drain-value">{formatPercent(session.drainPercent)}</span>
          <span className="tsl-session-drain-label">used</span>
        </div>
      </div>

      {points.length >= 2 ? (
        <BatteryChart points={points} height={104} showBucketFills interactive />
      ) : (
        <div className="tsl-chart-placeholder">No session points</div>
      )}

      {pillBuckets.length > 0 ? (
        <div className="tsl-pills">
          {pillBuckets.map((bucket, index) => {
            const kind = canonicalKind(bucket.kind);
            const meta = bucketMeta[kind];
            return (
              <span
                key={`${bucket.kind}-${index}`}
                className="tsl-bucket-pill"
                style={{ color: meta.tint, background: `color-mix(in srgb, ${meta.tint} 12%, transparent)` }}
              >
                <span className="tsl-pill-dot" style={{ background: meta.tint }} />
                {meta.short} {formatPercent(bucket.percent)}
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/* Drain rows (spec §5.7) ------------------------------------------------------ */

function DrainRows({ buckets, totalDrainPercent }: { buckets: TeslaBatteryDrainBucket[]; totalDrainPercent: number }) {
  const merged = new Map<BucketKind, { percent: number; minutes: number; miles: number; label: string | null }>();
  for (const bucket of buckets) {
    const kind = canonicalKind(bucket.kind);
    const entry = merged.get(kind) ?? { percent: 0, minutes: 0, miles: 0, label: null };
    entry.percent += bucket.percent;
    entry.minutes += bucket.minutes ?? 0;
    entry.miles += bucket.miles ?? 0;
    if (kind === "other" && bucket.label) entry.label = entry.label ?? bucket.label;
    merged.set(kind, entry);
  }

  return (
    <div className="tsl-drain-rows">
      {preferredBucketOrder.map((kind) => {
        const meta = bucketMeta[kind];
        const entry = merged.get(kind) ?? { percent: 0, minutes: 0, miles: 0, label: null };
        const parts: string[] = [];
        if (entry.minutes > 0) parts.push(`${Math.round(entry.minutes)} min`);
        if (entry.miles > 0) parts.push(`${entry.miles.toFixed(1)} mi`);
        const detail = parts.length ? parts.join(" / ") : "No recorded drain";
        const fillRatio = totalDrainPercent > 0 ? Math.min(1, Math.max(0, entry.percent / totalDrainPercent)) : 0;
        const Icon = meta.icon;
        return (
          <div key={kind} className="tsl-drain-row">
            <div className="tsl-drain-head">
              <span
                className="tsl-drain-icon"
                style={{ color: meta.tint, background: `color-mix(in srgb, ${meta.tint} 14%, transparent)` }}
              >
                <Icon size={13} strokeWidth={2.8} />
              </span>
              <span className="tsl-drain-text">
                <span className="tsl-drain-label">{kind === "other" ? entry.label ?? meta.label : meta.label}</span>
                <span className="tsl-drain-detail">{detail}</span>
              </span>
              <span className="tsl-drain-percent">{formatPercent(entry.percent)}</span>
            </div>
            <div className="tsl-drain-track">
              <div className="tsl-drain-fill" style={{ width: `${fillRatio * 100}%`, background: meta.tint }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Battery chart with bucket fills, scrubber, tooltip (spec §5.6 / §5.7) -------- */

interface ChartPoint {
  /** ISO timestamp for real points, or a synthetic label like "start". */
  label: string;
  batteryLevel: number;
  bucket: BucketKind | null;
}

const chartInsets = { top: 10, left: 32, bottom: 28, right: 8 };

function BatteryChart({
  points,
  height,
  showBucketFills,
  interactive
}: {
  points: ChartPoint[];
  height: number;
  showBucketFills: boolean;
  interactive: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [scrubIndex, setScrubIndex] = useState<number | null>(null);
  const scrubbing = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const n = points.length;
  const plotW = Math.max(0, width - chartInsets.left - chartInsets.right);
  const plotH = height - chartInsets.top - chartInsets.bottom;
  const plotBottom = chartInsets.top + plotH;

  const levels = points.map((p) => p.batteryLevel);
  const rawMin = Math.min(...levels);
  const rawMax = Math.max(...levels);
  const yMin = Math.max(0, Math.floor(rawMin - 2));
  const yMax = Math.max(yMin + 1, Math.min(100, Math.ceil(rawMax + 2)));

  const x = (index: number) => chartInsets.left + (n <= 1 ? plotW / 2 : (index * plotW) / (n - 1));
  const y = (level: number) => chartInsets.top + ((yMax - level) / (yMax - yMin)) * plotH;

  function indexFromEvent(event: ReactPointerEvent<SVGSVGElement>): number {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const step = n <= 1 ? plotW : plotW / (n - 1);
    const raw = step > 0 ? Math.round((px - chartInsets.left) / step) : 0;
    return Math.max(0, Math.min(n - 1, raw));
  }

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    if (!interactive) return;
    scrubbing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    setScrubIndex(indexFromEvent(event));
  }

  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    if (!interactive || !scrubbing.current) return;
    setScrubIndex(indexFromEvent(event));
  }

  function endScrub() {
    scrubbing.current = false;
    setScrubIndex(null);
  }

  if (width <= 0) {
    return <div ref={wrapRef} className="tsl-chart-wrap" style={{ height }} />;
  }

  // Segment i (points[i] → points[i+1]) belongs to points[i+1].bucket ?? points[i].bucket.
  const segmentBucket = (index: number): BucketKind | null =>
    points[index + 1].bucket ?? points[index].bucket ?? null;

  // Consecutive same-bucket runs for the area fills.
  const runs: { start: number; end: number; kind: BucketKind }[] = [];
  if (showBucketFills) {
    for (let i = 0; i < n - 1; i += 1) {
      const kind = segmentBucket(i);
      if (kind === null) continue;
      const last = runs[runs.length - 1];
      if (last && last.kind === kind && last.end === i) {
        last.end = i + 1;
      } else {
        runs.push({ start: i, end: i + 1, kind });
      }
    }
  }

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.batteryLevel)}`).join(" ");
  const gridFractions = [0, 1 / 3, 2 / 3, 1];
  const midIndex = Math.floor((n - 1) / 2);
  const labelIndices = [...new Set([0, midIndex, n - 1])];

  const scrub = scrubIndex !== null && scrubIndex < n ? scrubIndex : null;
  let tooltip: { tx: number; line1: string; line2: string; tint: string } | null = null;
  if (scrub !== null) {
    const enteringBucket = points[scrub].bucket ?? (scrub > 0 ? points[scrub - 1].bucket : null);
    const short = enteringBucket ? bucketMeta[enteringBucket].short : "Start";
    tooltip = {
      tx: Math.max(76, Math.min(width - 76, x(scrub))),
      line1: tooltipTimestamp(points[scrub].label),
      line2: `${short} ${Math.round(points[scrub].batteryLevel)}%`,
      tint: enteringBucket ? bucketMeta[enteringBucket].tint : "var(--muted)"
    };
  }
  const tooltipWidth = tooltip ? Math.max(tooltip.line1.length, tooltip.line2.length + 2) * 5.8 + 18 : 0;

  return (
    <div ref={wrapRef} className="tsl-chart-wrap">
      <svg
        className="tsl-chart"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Battery level chart"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endScrub}
        onPointerCancel={endScrub}
        onPointerLeave={endScrub}
      >
        {gridFractions.map((fraction) => {
          const gy = chartInsets.top + fraction * plotH;
          const value = Math.round(yMax - fraction * (yMax - yMin));
          return (
            <g key={fraction}>
              <line
                x1={chartInsets.left}
                x2={width - chartInsets.right}
                y1={gy}
                y2={gy}
                stroke="var(--stroke)"
                strokeOpacity={fraction === 1 ? 1 : 0.72}
              />
              <text
                x={chartInsets.left - 6}
                y={gy + 3}
                textAnchor="end"
                fontSize={10}
                fontWeight={800}
                fill="var(--muted)"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {value}%
              </text>
            </g>
          );
        })}

        {runs.map((run, index) => {
          const top = points
            .slice(run.start, run.end + 1)
            .map((p, i) => `${i === 0 ? "M" : "L"}${x(run.start + i)},${y(p.batteryLevel)}`)
            .join(" ");
          const path = `${top} L${x(run.end)},${plotBottom} L${x(run.start)},${plotBottom} Z`;
          return <path key={index} d={path} fill={bucketMeta[run.kind].tint} fillOpacity={0.18} />;
        })}

        <path d={linePath} fill="none" stroke="var(--cool)" strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" />

        {scrub === null ? (
          <circle cx={x(n - 1)} cy={y(points[n - 1].batteryLevel)} r={4} fill="var(--surface)" stroke="var(--cool)" strokeWidth={2} />
        ) : (
          <>
            <line
              x1={x(scrub)}
              x2={x(scrub)}
              y1={chartInsets.top}
              y2={plotBottom}
              stroke="var(--ink)"
              strokeOpacity={0.28}
              strokeDasharray="4 4"
            />
            <circle
              cx={x(scrub)}
              cy={y(points[scrub].batteryLevel)}
              r={4.5}
              fill="var(--surface)"
              stroke={tooltip?.tint ?? "var(--muted)"}
              strokeWidth={2}
            />
          </>
        )}

        {labelIndices.map((index) => (
          <text
            key={index}
            x={x(index)}
            y={plotBottom + 18}
            textAnchor={index === 0 ? "start" : index === n - 1 ? "end" : "middle"}
            fontSize={10}
            fontWeight={800}
            fill="var(--muted)"
          >
            {axisLabel(points[index].label)}
          </text>
        ))}

        {tooltip ? (
          <g>
            <rect
              x={tooltip.tx - tooltipWidth / 2}
              y={6}
              width={tooltipWidth}
              height={34}
              rx={10}
              fill="var(--ink)"
              fillOpacity={0.92}
            />
            <text x={tooltip.tx - tooltipWidth / 2 + 9} y={19} fontSize={10} fontWeight={900} fill="var(--surface)">
              {tooltip.line1}
            </text>
            <circle cx={tooltip.tx - tooltipWidth / 2 + 12} cy={30.5} r={3} fill={tooltip.tint} />
            <text x={tooltip.tx - tooltipWidth / 2 + 19} y={34} fontSize={10} fontWeight={900} fill="var(--surface)">
              {tooltip.line2}
            </text>
          </g>
        ) : null}
      </svg>
    </div>
  );
}
